import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';
import {
    getRoundedPrice,
    usePdpPriceRoundingClientVariant,
} from 'dibs-buyer-price/exports/priceDisplayRounding';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpSharedBuyItNowButton } from '../shared/PdpSharedShoppingButtons/PdpSharedBuyItNowButton';

// types
import { PdpSharedAuctionActionBinAndBid_item$key as Item } from '../PdpSharedAuctionActions/__generated__/PdpSharedAuctionActionBinAndBid_item.graphql';
import { PdpSharedAuctionActionBinAndBid_viewer$key } from '../PdpSharedAuctionActions/__generated__/PdpSharedAuctionActionBinAndBid_viewer.graphql';
import { OnAuctionBidClick } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';

// styles
import styles from './PdpSharedAuctionActionBinAndBid.scss';

type Props = {
    viewer: PdpSharedAuctionActionBinAndBid_viewer$key;
    item: Item;
    currency: string;
    isBiddingDisabled?: boolean;
    onAuctionBidClick: OnAuctionBidClick;
    handleRoundPriceTracking?: () => void;
};

type RelayVariables = {
    isClient: boolean;
    userId: string;
    itemId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const viewerFragment = graphql`
    fragment PdpSharedAuctionActionBinAndBid_viewer on Viewer {
        isMultiItemCart: featureFlag(feature: "multiItemCart")
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBinAndBid_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedPurchaseButton_item
        ...OriginalListPrice_item
        ...PdpSharedAuctionCountdown_item
        ...PdpSharedBuyItNowButton_item
        relevantAuctionSolution(page: pdp) {
            hasReserve
        }
    }
`;

const copy = {
    startingBid: (
        <FormattedMessage
            id="PdpRespAuctionActionBinAndBid.startingBid"
            defaultMessage="Starting Bid"
        />
    ),
    buyItNow: (
        <FormattedMessage id="PdpRespAuctionActionBinAndBid.buyItNow" defaultMessage="Buy It Now" />
    ),
    noReserve: (
        <FormattedMessage
            id="PdpRespAuctionActionBinAndBid.noReserve"
            defaultMessage="No Reserve"
        />
    ),
};

export const PdpSharedAuctionActionBinAndBid: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    currency,
    onAuctionBidClick,
    isBiddingDisabled,
    handleRoundPriceTracking,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const { isMultiItemCart } = viewer;
    const { hasReserve } = item?.relevantAuctionSolution || {};
    const { userId, itemId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );

    const auctionPricing = useAuctionPricing({ item, currency });

    const isPdpPriceRoundingClientVariant = usePdpPriceRoundingClientVariant();
    const buyItNowAmount = isPdpPriceRoundingClientVariant
        ? getRoundedPrice(auctionPricing.buyItNowAmount || 0)
        : auctionPricing.buyItNowAmount;

    return (
        <>
            <OriginalListPrice
                item={item}
                currency={currency}
                className={styles.originalListPrice}
            />
            {!hasReserve && (
                <span data-tn="pdp-item-pricing-label-no-reserve" className={styles.noReserve}>
                    {copy.noReserve}
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <div className={styles.container}>
                <div className={styles.priceRow}>
                    <span data-tn="pdp-item-pricing-label-starting-bid" className={styles.boldCopy}>
                        {copy.startingBid}
                    </span>
                    <PriceDisplay
                        currency={currency}
                        item={item}
                        sku={null}
                        useSvgIcon
                        roundPrice
                        handleRoundPriceTracking={handleRoundPriceTracking}
                    />
                </div>
                <div className={styles.printHide}>
                    <PdpSharedAuctionBidButton
                        className={styles.priceButton}
                        onClick={() => onAuctionBidClick()}
                        disabled={isBiddingDisabled}
                    />
                </div>

                <div className={styles.priceRow}>
                    <span data-tn="pdp-item-pricing-label-buy-it-now" className={styles.boldCopy}>
                        {copy.buyItNow}
                        <PdpSharedAuctionTooltipLazy type="buyItNow" />
                    </span>
                    <div data-tn="pdp-item-buy-it-now-price" className={styles.price}>
                        {buyItNowAmount && (
                            <FormattedPrice value={buyItNowAmount} currency={currency} />
                        )}
                    </div>
                </div>
                <div className={styles.printHide}>
                    {isMultiItemCart ? (
                        <PdpSharedBuyItNowButton
                            variant="auction"
                            item={item}
                            disabled={isBiddingDisabled}
                        />
                    ) : (
                        <PdpSharedPurchaseButton
                            item={item}
                            itemId={itemId}
                            userId={userId}
                            isPrimary={false}
                            quantitySelected={'1'}
                            disabled={isBiddingDisabled}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
